import React, { useState, useEffect, useCallback } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import ReadyFilters, { useReadyFilters } from '../../components/ReadyFilters';

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => { const v=Math.abs(n||0); return v ? v.toLocaleString('fa-IR') : '•'; };
const si = v => { try{return parseInt(toEng(String(v||0)),10)||0;}catch{return 0;} };

/* فراخوانی API + رفرش توکن */
async function API(path,opts={}){
  const doFetch=()=>fetch(`/api/accounting${path}`,{headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts});
  let res=await doFetch();
  if(res.status===401){const rr=await fetch('/api/auth/token/refresh/',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({refresh:localStorage.getItem('refresh')})});if(rr.ok){const d=await rr.json();localStorage.setItem('access',d.access);res=await doFetch();}}
  return res.json();
}

/* انتخاب سند هزینهٔ آماده برای افزودن به سند */
export default function HazinehReadyModal({ year, onClose, onAdded }) {
  const [rawRows,  setRawRows]  = useState([]);
  const [selected, setSelected] = useState(null);
  const [readyNo,  setReadyNo]  = useState('');
  const [docDate,  setDocDate]  = useState('');
  // فیلترهای همزمان: جستجو + هزینه‌ای/تملک + سال مالی (سمت سرور)
  const f = useReadyFilters({ initialFy: year });
  const [serverTotal,   setServerTotal]   = useState(0);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');

  /* ── ریز اقلام ── */
  const [detRows,        setDetRows]        = useState([]);
  const [detTotal,       setDetTotal]       = useState(0);
  const [detHasMore,     setDetHasMore]     = useState(false);
  const [detPage,        setDetPage]        = useState(1);
  const [detLoading,     setDetLoading]     = useState(false);
  const [detLoadingMore, setDetLoadingMore] = useState(false);

  /* عرض ستون‌ها */
  const { TH }        = useColumnResize([55,60,70,80,70,70,190,95,90,90,80,100,90,100,80,85,55]);
  /* عرض ستون‌های ریز اقلام */
  const { TH: DTH }   = useColumnResize([210,70,170,120,100,80,85,60,110,115,105,95,120,100,115]);

  const {
    visibleRows,
    shownCount, LoadMoreButton,
  } = useTablePagination({
    allRows: rawRows, pageSize: 200,
    searchFields: [],   // جستجو سمت سرور انجام می‌شود
    serverTotal, serverHasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),4000); };

  useEffect(() => {
    API(`/documents/next-no/?year=${year}`).then(j => { if (j.ok) setDocDate(j.last_date||''); });
  }, [year]);

  const qs = useCallback((page) => {
    const p = [`page=${page}`];
    if (f.query) p.push(f.query);
    return p.join('&');
  }, [f.query]);

  /* خواندن فهرست از سرور */
  const load = useCallback(async () => {
    setLoading(true); setError(''); setRawRows([]); setSelected(null);
    setDetRows([]); setDetTotal(0); setDetHasMore(false);
    const j = await API(`/auto-document/hazineh/ready/?${qs(1)}`);
    setLoading(false);
    if (j.ok) { setRawRows(j.data); setServerTotal(j.total||j.count||0); setServerHasMore(j.has_more||false); setCurrentPage(1); }
    else setError(j.msg);
  }, [qs]);

  /* ۲۰۰ ردیف بعدی */
  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    const j = await API(`/auto-document/hazineh/ready/?${qs(nextPage)}`);
    setLoadingMore(false);
    if (j.ok) {
      setRawRows(prev => [...prev, ...j.data]);
      setServerTotal(j.total||0);
      setServerHasMore(j.has_more||false);
      setCurrentPage(nextPage);
    }
  };

  useEffect(() => { load(); }, [load]);

  const sel = selected ? rawRows.find(r => r.hazineh_document_id === selected) : null;
  const isNewDoc = si(readyNo) === 0;

  /* ── ریز اقلام سند هزینه‌ی انتخاب‌شده ── */
  const detQs = (row, page) =>
    `hazineh_document_id=${row.hazineh_document_id}&program_kind=${row.program_kind}` +
    `&financial_year=${row.financial_year||year}&page=${page}`;

  /* ریز ردیف انتخاب‌شده */
  const loadDetail = useCallback(async (row) => {
    if (!row) { setDetRows([]); setDetTotal(0); setDetHasMore(false); return; }
    setDetLoading(true); setDetRows([]);
    const j = await API(`/auto-document/hazineh/detail/?${detQs(row,1)}`);
    setDetLoading(false);
    if (j.ok) {
      setDetRows(j.data); setDetTotal(j.total||0); setDetHasMore(j.has_more||false); setDetPage(1);
    } else setError(j.msg);
  }, [year]);

  /* ۲۰۰ ریز بعدی */
  const loadDetailMore = async () => {
    if (!sel) return;
    const nextPage = detPage + 1;
    setDetLoadingMore(true);
    const j = await API(`/auto-document/hazineh/detail/?${detQs(sel,nextPage)}`);
    setDetLoadingMore(false);
    if (j.ok) {
      setDetRows(prev => [...prev, ...j.data]);
      setDetTotal(j.total||0); setDetHasMore(j.has_more||false); setDetPage(nextPage);
    }
  };

  /* انتخاب/لغو انتخاب ردیف */
  const pickRow = row => {
    const isSel = selected === row.hazineh_document_id;
    if (isSel) { setSelected(null); setDetRows([]); setDetTotal(0); setDetHasMore(false); }
    else { setSelected(row.hazineh_document_id); loadDetail(row); }
  };

  /* افزودن به سند */
  const addToDoc = async () => {
    if (!sel) { setError('سند هزینه‌ای انتخاب نشده'); return; }
    if (isNewDoc && !docDate) { setError('تاریخ سند جدید الزامیست'); return; }
    setSaving(true); setError('');
    const j = await API('/auto-document/hazineh/add/', {
      method:'POST',
      body: JSON.stringify({ financial_year:year, ready_document_no:si(readyNo), doc_date:docDate, row:sel }),
    });
    setSaving(false);
    if (j.ok) { showToast(j.msg); onAdded&&onAdded(); load(); } else setError(j.msg);
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={s.hdr}>
          <div>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#fff'}}>💵 سند هزینه — شناسایی سند هزینه</div>
            <div style={{fontSize:'11px',color:'rgba(255,255,255,0.6)',marginTop:'2px'}}>
              هزینه‌ای: ۶۱۰ / کد فصل — تملک: ۱۵۰/۱۵۰۰۱ — بستانکار: ۲۱۰/۲۱۰۰۱ — سال مالی {toFa(year)}
            </div>
          </div>
          <button onClick={onClose} style={s.xBtn}>✕</button>
        </div>

        <div style={s.body}>
          {error && <div style={s.err}>⚠️ {error}</div>}

          <div style={{...s.card, background:'#fffdf5', border:'1.5px solid #f0e6c0'}}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#8d6e00',marginBottom:'8px'}}>🧾 سند مقصد</div>
            <div style={{display:'flex',gap:'14px',flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={s.fld}>
                <label style={s.lbl}>شماره سند مقصد</label>
                <input value={toFa(readyNo)} onChange={e=>setReadyNo(toEng(e.target.value).replace(/\D/g,'').slice(0,6))}
                  placeholder="خالی = سند جدید" style={{...s.inp,width:'150px'}} />
              </div>
              {isNewDoc && (
                <div style={s.fld}>
                  <label style={s.lbl}>تاریخ سند جدید *</label>
                  <JalaliDatePicker value={docDate} onChange={v=>setDocDate(v)} placeholder="تاریخ"/>
                </div>
              )}
              <div style={{fontSize:'11px',color:'#555',flex:1,minWidth:'200px'}}>⚠ تک‌انتخابی — هر بار فقط یک سند هزینه</div>
            </div>
          </div>

          <ReadyFilters f={f}
            searchLabel="جستجو (شماره سند هزینه / سریال / شرح / دبیرخانه)">
            <button onClick={load} disabled={loading} style={s.btnS}>{loading?'⏳...':'↺ به‌روزرسانی'}</button>
            <span style={{fontSize:'11px',color:'#888'}}>{toFa(shownCount)} از {toFa(serverTotal)}</span>
          </ReadyFilters>

          {/* ── جدول اصلی: سندهای هزینه ── */}
          <div style={{maxHeight:'34vh',overflow:'auto',borderRadius:'8px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',direction:'rtl',fontSize:'11px',tableLayout:'fixed'}}>
              <thead>
                <tr>
                  <TH idx={0}>سال مالی</TH>
                  <TH idx={1}>سریال</TH>
                  <TH idx={2}>سندهزینه</TH>
                  <TH idx={3}>تاریخ</TH>
                  <TH idx={4}>نوع حساب</TH>
                  <TH idx={5}>نوع پرداخت</TH>
                  <TH idx={6} right>شرح</TH>
                  <TH idx={7} red>مبلغ</TH>
                  <TH idx={8}>مالیات بر ارزش افزوده</TH>
                  <TH idx={9}>بیمه سهم دولت</TH>
                  <TH idx={10}>تخفیف</TH>
                  <TH idx={11} red>جمع هزینه</TH>
                  <TH idx={12}>کسورات</TH>
                  <TH idx={13} red>خالص</TH>
                  <TH idx={14}>شماره دبیرخانه</TH>
                  <TH idx={15}>تاریخ دبیرخانه</TH>
                  <TH idx={16}>اقلام</TH>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r,i)=>{
                  const isSel = selected === r.hazineh_document_id;
                  return (
                  <tr key={r.hazineh_document_id} onClick={()=>pickRow(r)}
                    style={{background:isSel?'#e8f5e9':i%2===0?'#fff':'#fafafa',cursor:'pointer'}}>
                    <td style={s.td}>{toFa(r.financial_year)}</td>
                    <td style={s.td}>{toFa(r.user_serial)}</td>
                    <td style={{...s.td,fontWeight:'700'}}>{toFa(r.hazineh_document_no)}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{r.hazineh_document_date||'—'}</td>
                    <td style={{...s.td,fontSize:'10px',color:r.program_kind===0?'#1565c0':'#4a148c'}}>{r.program_kind_title}</td>
                    <td style={{...s.td,fontSize:'10px'}}>{r.pay_kind_title}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.remark}>{r.remark||'—'}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>{numFmt(r.sum_pay)}</td>
                    <td style={{...s.td,fontFamily:'monospace',color:'#e65100'}}>{numFmt(r.added_tax)}</td>
                    <td style={{...s.td,fontFamily:'monospace',color:'#1565c0'}}>{numFmt(r.added_insure)}</td>
                    <td style={{...s.td,fontFamily:'monospace',color:'#795548'}}>{numFmt(r.detail_discount)}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>{numFmt(r.total_cost)}</td>
                    <td style={{...s.td,fontFamily:'monospace',color:'#4a148c'}}>{numFmt(r.deduction)}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#1b5e20'}}>{numFmt(r.net_amount)}</td>
                    <td style={{...s.td,fontSize:'10px'}}>{r.letter_no||'—'}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{r.letter_date||'—'}</td>
                    <td style={s.td}>{toFa(r.item_count)}</td>
                  </tr>);})}
                {!loading && visibleRows.length===0 && (
                  <tr><td colSpan={17} style={{...s.td,padding:'24px',color:'#999'}}>📭 هیچ سند هزینه‌ای برای ثبت سند شناسایی ارسال نشده است</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <LoadMoreButton onClick={loadMore} loading={loadingMore} />

          {/* ── جدول ریز اقلام ── */}
          {sel && (
            <>
              <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap',flexShrink:0}}>
                <span style={{fontSize:'12px',fontWeight:'700',color:'#8d6e00'}}>
                  📋 ریز اقلام سند هزینه شماره {toFa(sel.hazineh_document_no)}
                </span>
                <span style={{fontSize:'11px',color:'#888'}}>
                  {detLoading ? '⏳ در حال بارگذاری...' : `${toFa(detRows.length)} از ${toFa(detTotal)}`}
                </span>
              </div>

              <div style={{maxHeight:'30vh',overflow:'auto',borderRadius:'8px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)'}}>
                <table style={{width:'100%',borderCollapse:'collapse',direction:'rtl',fontSize:'11px',tableLayout:'fixed'}}>
                  <thead>
                    <tr>
                      <DTH idx={0} gold right>شرح</DTH>
                      <DTH idx={1} gold>روش پرداخت</DTH>
                      <DTH idx={2} gold right>فروشنده</DTH>
                      <DTH idx={3} gold>حساب</DTH>
                      <DTH idx={4} gold>بانک</DTH>
                      <DTH idx={5} gold>فاکتور</DTH>
                      <DTH idx={6} gold>تاریخ</DTH>
                      <DTH idx={7} gold>تعداد</DTH>
                      <DTH idx={8} gold>مبلغ واحد</DTH>
                      <DTH idx={9} gold>مبلغ</DTH>
                      <DTH idx={10} gold>مالیات بر ارزش افزوده</DTH>
                      <DTH idx={11} gold>تخفیف</DTH>
                      <DTH idx={12} gold>جمع</DTH>
                      <DTH idx={13} gold>کسورات</DTH>
                      <DTH idx={14} gold>خالص</DTH>
                    </tr>
                  </thead>
                  <tbody>
                    {detRows.map((g,i)=>(
                      <tr key={g.hazineh_document_detail_id} style={{background:i%2===0?'#fffdf7':'#fff'}}>
                        <td style={{...s.td,textAlign:'right'}} title={g.remark}>{g.remark||'—'}</td>
                        <td style={{...s.td,fontSize:'10px'}}>{g.payment_method_title}</td>
                        <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={g.receiver_name}>{g.receiver_name||'—'}</td>
                        <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{g.account_no||'—'}</td>
                        <td style={{...s.td,fontSize:'10px'}}>{g.bank_name||'—'}</td>
                        <td style={{...s.td,fontSize:'10px'}}>{g.factor_no||'—'}</td>
                        <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{g.factor_date||'—'}</td>
                        <td style={s.td}>{toFa(g.quantity)}</td>
                        <td style={{...s.td,fontFamily:'monospace'}}>{numFmt(g.unit_price)}</td>
                        <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>{numFmt(g.sum_pay)}</td>
                        <td style={{...s.td,fontFamily:'monospace',color:'#e65100'}}>{numFmt(g.added_tax)}</td>
                        <td style={{...s.td,fontFamily:'monospace',color:'#795548'}}>{numFmt(g.discount)}</td>
                        <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>{numFmt(g.total_amount)}</td>
                        <td style={{...s.td,fontFamily:'monospace',color:'#4a148c'}}>{numFmt(g.deduction)}</td>
                        <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#1b5e20'}}>{numFmt(g.net_amount)}</td>
                      </tr>
                    ))}
                    {!detLoading && detRows.length===0 && (
                      <tr><td colSpan={15} style={{...s.td,padding:'18px',color:'#999'}}>📭 ریز قلمی ثبت نشده است</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {detHasMore && (
                <div style={{textAlign:'center',padding:'10px 0',flexShrink:0}}>
                  <button onClick={loadDetailMore} disabled={detLoadingMore}
                    style={{padding:'9px 36px',background:detLoadingMore?'#ccc':'linear-gradient(135deg,#0d2137,#1a2f50)',
                      color:'#fff',border:'none',borderRadius:'8px',cursor:detLoadingMore?'default':'pointer',
                      fontFamily:'inherit',fontSize:'13px',fontWeight:'700'}}>
                    {detLoadingMore ? '⏳ در حال بارگذاری...'
                      : `▼ ۲۰۰ ریز قلم بعدی (${toFa(detTotal-detRows.length)} باقی‌مانده)`}
                  </button>
                </div>
              )}
            </>
          )}

          <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap',flexShrink:0}}>
            {sel && <>
              <span style={s.badge('#1a2f50')}>سندهزینه {toFa(sel.hazineh_document_no)}</span>
              <span style={s.badge(sel.program_kind===0?'#1565c0':'#4a148c')}>{sel.program_kind_title}</span>
              <span style={s.badge('#b71c1c')}>جمع هزینه: {numFmt(sel.total_cost)}</span>
              <span style={s.badge('#1b5e20')}>خالص: {numFmt(sel.net_amount)}</span>
              <span style={s.badge('#555')}>اقلام: {toFa(sel.item_count)}</span>
            </>}
            <button onClick={addToDoc} disabled={saving||!sel}
              style={{...s.btnP,marginRight:'auto',opacity:(saving||!sel)?0.4:1}}>
              {saving?'⏳...':(isNewDoc?'💾 ساخت سند شناسایی':'💾 افزودن به سند')}
            </button>
          </div>
        </div>

        {toast && <div style={{position:'absolute',bottom:'14px',right:'14px',background:'#1b5e20',
          color:'#fff',padding:'10px 20px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',zIndex:10}}>{toast}</div>}
      </div>
    </div>
  );
}

/* استایل‌ها */
const s = {
  overlay:   {position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:'12px'},
  modal:     {background:'#f0f2f7',borderRadius:'14px',width:'100%',maxWidth:'1500px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,0.5)',overflow:'hidden',position:'relative'},
  hdr:       {display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:'14px 18px',background:'linear-gradient(135deg,#0a1628,#1565c0)',flexShrink:0},
  xBtn:      {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'28px',height:'28px',borderRadius:'50%',cursor:'pointer',fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center'},
  body:      {flex:1,overflowY:'auto',padding:'14px',display:'flex',flexDirection:'column',gap:'10px'},
  err:       {background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'7px',padding:'8px 12px',color:'#c62828',fontSize:'12px'},
  card:      {background:'#fff',borderRadius:'10px',padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.08)'},
  fld:       {display:'flex',flexDirection:'column',gap:'3px'},
  lbl:       {fontSize:'11px',fontWeight:'600',color:'#555'},
  inp:       {padding:'7px 10px',border:'1.5px solid #e0e0e0',borderRadius:'7px',fontSize:'12px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box'},
  td:        {padding:'5px 8px',fontSize:'11px',textAlign:'center',borderBottom:'1px solid #f0f0f0',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  btnP:      {padding:'9px 20px',background:'linear-gradient(135deg,#1565c0,#1976d2)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnS:      {padding:'8px 14px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px'},
  badge:     bg => ({padding:'4px 10px',background:bg,color:'#fff',borderRadius:'16px',fontSize:'11px',fontWeight:'700'}),
};
